import React, { Component, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import translations from '@shopify/polaris/locales/es.json';
import { Page, AppProvider, Banner, Modal, PageActions, Button } from '@shopify/polaris';
import ProductFormWrapper from '@container/product/ProductFormWrapper';
// import ProductFormWrapper from '../../container/product/ProductFormWrapper';
import Skeleton from '@presentational/SkeletonPageGeneric';

import pick from 'lodash/pick';

import { tModel, checkDefaultForPending } from '@utils';
import { loadRemoteFiles } from '@utils/recordHelpers'
import axios from '@utils/axios';

const propTypes = {
  product: PropTypes.object.isRequired,
  readOnly: PropTypes.bool,
  submitURL: PropTypes.string.isRequired,
  variantsURL: PropTypes.string.isRequired,
  requestSyncURL: PropTypes.string.isRequired,
  init_image_order: PropTypes.array,
};

const defaultProps = {
  init_image_order: [],
}

const syncRequestStatus = {
  standby: "STANDBY", requested: "DONE", duplicate: "DUPLICATE"
}

const loadProductFiles = async (product, callback) => {
  try {
    let files = await loadRemoteFiles(product.local_images);
    callback(files);
  } catch (error) {
    alert("Ocurrio un error por favor recargue el sitio.")
  }
}

// ===========> Component <===================
const ProductShow = (props) => {
  const [loadingData, setLoadingData] = useState({
    loading: true, localImages: []
  });

  const [requestData, setRequestData] = useState({ 
    modalOpen: false, requested: false, message: "", 
    status: syncRequestStatus.standby , loading: false,
  })

  const { product, ...moreProps } = props;
  const variantsCount = product.variants.length;
  const pendingVariantsSync = variantsCount < 1 && !!product.shopify_id; // All variants have been deleted on app and default_variant hasn't been synced with Shopify (it will always exists if no other variant is around)
  
  let productDefaultVariantAttributes = {};
  const hasDefaultVariant = checkDefaultForPending(product)

  // If has default shopify variant set the values to the 'main' product for view otherwise they would be 'hidden' in the default variant.
  // If it doesn't have variant means it is a pending first publish product.
  // OPTIMIZE this using the serializer default_variant_data
  if(hasDefaultVariant && variantsCount > 0){
    var defaultVariantObject = product.variants[0];
    productDefaultVariantAttributes = {
      ...pick(defaultVariantObject, ['weight', 'weight_unit', 'sku', 'barcode']),
      publish_price: defaultVariantObject.price,
      barulu_cost:  defaultVariantObject.unit_cost
    }
  }

  const parsedProduct = {
    ...product,
    images: product.images || [],
    ...productDefaultVariantAttributes,
    variantsEnabled: !hasDefaultVariant,
    local_images: loadingData.localImages,
  }

  // Load remote images as js blob objects. Only once in the render cycle, when loading is true.
  useEffect( () => {
    if(!pendingVariantsSync){ // If pending variant sync. Do not load anything. Product shouldn't be updated. STAY always loading
      if(loadingData.loading){
        function loadCallback(files){
          setLoadingData({
            loading: false, localImages: files
          })
        }
  
        loadProductFiles(product, loadCallback); 
      }
    }
  })

  // Functions ========================================
  const requestSync = async () => {
    try {
      setRequestData({ ...requestData, loading: true })
      let response = await axios.post(props.requestSyncURL, {});
      const data = response.data;
      if(data.message){
        setRequestData({
          requested: true,
          message: data.message,
          status: data.status,
          modalOpen: true,
          loading: false,
          duplicateURL: data.duplicateURL, // if duplicates exists
        })
      }
    } catch (error) {
      alert("Ocurrio un error al conectarse con el servidor.")
      setRequestData({ ...requestData, loading: false })
    }
  }

  const handleDelete = async () => {
    if (confirm('Esta acción es irreversible, continuar?')) {
      // Save it!
      await setRequestData({ ...requestData, loading: true });
      try {
        const response = await axios({
          method: "DELETE",
          url: props.deleteURL,
          data: { skip_shopify_delete: "true" }
        });
        const { data } = response;
        if(data.success){
          alert("Eliminado con éxito");
          window.location = data.redirectURL;
        }
        else{
          alert("Ocurrio un error.");
        }
      } catch (error) {
        alert("Ocurrio un error.")
      }
      await setRequestData({ ...requestData, loading: false });
    } else {
      // Do nothing!
    }
  }
  // Render ===================================================
  if(!loadingData.loading){
    return(
      <ProductFormWrapper 
        {...moreProps}
        update
        readOnly={ props.readOnly }
        title={ parsedProduct.title }
        product={ parsedProduct }
        hasDefaultVariant={ hasDefaultVariant }
        defaultVariant={ defaultVariantObject }
        init_image_order={props.init_image_order}
      />
    )
  }
  else{
    const duplicateIssue = requestData.status == syncRequestStatus.duplicate;
    return (
      <AppProvider i18n={translations}>
        <div>
          {/* Set banner if product is missing variants and should have at least one */}
          {/* TODO Add seller name as page subtitle when Admin */}
          { !!pendingVariantsSync &&
            <Page>
              <Banner 
                title="Sincronización pendiente" 
                status="info"
                action={{
                  content: 'Solicitar sincronización', 
                  onAction: requestSync ,
                  disabled: requestData.requested, loading: requestData.loading
                }}
              >
                <p>
                  El producto necesita sincronizarse antes de hacer cambios. Por favor espere unos minutos e intente recargar el sitio.
                </p>
              </Banner>
  
              <Modal
                title="Sincronización"
                large
                open={ requestData.modalOpen }
                onClose={ () => setRequestData({ ...requestData, modalOpen: false }) }
              >
                <Banner 
                  title={ requestData.message } 
                  status={ duplicateIssue ? "warning" : "info" }
                  style={{ padding: 20 }}
                  >
                    { duplicateIssue &&
                      <PageActions
                        primaryAction={{
                          content: "Ver producto duplicado",
                          url: requestData.duplicateURL,
                          external: true,
                          loading: requestData.loading
                        }}
                        secondaryActions={[{
                          destructive: true,
                          content: "Eliminar este producto",
                          onAction: handleDelete,
                          loading: requestData.loading
                        }]}
                      />
                    }
                </Banner>
              </Modal>
            </Page>
          }
          {/* Feedback indicator */}
          <Skeleton />
        </div>
      </AppProvider>
    )
  }
}


ProductShow.propTypes = propTypes;
ProductShow.defaultProps = defaultProps;

export default ProductShow;
